import {computed, DestroyRef, Directive, effect, ElementRef, inject, input, signal} from '@angular/core';
import {DsTooltipDirective} from '@bmw-ds/components';
import {RotationImage} from '../../core/cosy/rotation-image';
import {Util} from '../../util/util';

interface CachedImage {
  readonly image: HTMLImageElement;
  loaded: boolean;
}

@Directive({
  selector: 'img[appVehiclePreviewRotation]',
  exportAs: 'appVehiclePreviewRotation',
  host: {
    '[src]': 'imageUrl()',
    '[class.is-rotatable]': 'canRotate()',
    '[class.is-rotating]': 'isDragging()',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointermove)': 'onPointerMove($event)',
    '(pointerup)': 'onPointerEnd($event)',
    '(pointercancel)': 'onPointerEnd($event)',
    '(lostpointercapture)': 'onPointerEnd($event)',
    '(window:blur)': 'endInteraction()',
    '(click)': 'onClick($event)',
    '(dragstart)': '$event.preventDefault()'
  }
})
export class VehiclePreviewRotationDirective {
  private static readonly PIXELS_PER_FRAME: number = 8;

  readonly appVehiclePreviewRotation = input<readonly RotationImage[]>([]);
  readonly vehiclePreviewImageUrl = input.required<string>();
  readonly isInteracting = signal<boolean>(false);
  readonly isDragging = signal<boolean>(false);

  private readonly element: ElementRef<HTMLImageElement> = inject(ElementRef);
  private readonly tooltip: DsTooltipDirective | null = inject(DsTooltipDirective, {optional: true, self: true});
  private readonly selectedImageUrl = signal<string | null>(null);
  private readonly rotationFailed = signal<boolean>(false);
  private readonly images: Map<string, CachedImage> = new Map();
  private pointerId: number | null = null;
  private startPosition: number = 0;
  private startFrame: number = 0;
  private selectedFrame: number = 0;
  private requestedImageUrl: string | null = null;
  private suppressClick: boolean = false;

  readonly imageUrl = computed((): string => this.selectedImageUrl() ?? this.vehiclePreviewImageUrl());
  readonly canRotate = computed((): boolean => this.appVehiclePreviewRotation().length > 1 && !this.rotationFailed());

  constructor() {
    effect((): void => {
      this.appVehiclePreviewRotation();
      this.vehiclePreviewImageUrl();
      this.endInteraction();
      this.clearImages();
      this.selectedFrame = 0;
      this.selectedImageUrl.set(null);
      this.rotationFailed.set(false);
    });
    inject(DestroyRef).onDestroy((): void => {
      this.endInteraction();
      this.clearImages();
    });
  }

  onPointerDown(event: PointerEvent): void {
    this.suppressClick = false;
    if (!this.canRotate() || event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary || this.pointerId !== null) {
      return;
    }
    this.pointerId = event.pointerId;
    this.startPosition = event.clientX;
    this.startFrame = this.selectedFrame;
    this.isInteracting.set(true);
    this.tooltip?.hideTooltip();
    this.element.nativeElement.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  }

  onPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) {
      return;
    }
    if ((event.buttons & 1) === 0) {
      this.endInteraction();
      return;
    }
    const steps: number = Math.trunc((event.clientX - this.startPosition) / VehiclePreviewRotationDirective.PIXELS_PER_FRAME);
    if (steps !== 0) {
      this.isDragging.set(true);
      this.suppressClick = true;
    }
    if (!this.isDragging()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const frameCount: number = this.appVehiclePreviewRotation().length;
    const frame: number = ((this.startFrame + steps) % frameCount + frameCount) % frameCount;
    this.showFrame(frame);
  }

  onPointerEnd(event: PointerEvent): void {
    if (event.pointerId === this.pointerId) {
      this.endInteraction();
    }
  }

  endInteraction(): void {
    const pointerId: number | null = this.pointerId;
    this.pointerId = null;
    this.isInteracting.set(false);
    this.isDragging.set(false);
    if (pointerId !== null && this.element.nativeElement.hasPointerCapture(pointerId)) {
      this.element.nativeElement.releasePointerCapture(pointerId);
    }
  }

  onClick(event: MouseEvent): void {
    if (this.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }
  }

  private showFrame(frame: number): void {
    this.selectedFrame = frame;
    const imageUrl: string = this.appVehiclePreviewRotation()[frame].imageUrl;
    this.requestedImageUrl = imageUrl;
    if (imageUrl === this.vehiclePreviewImageUrl()) {
      this.selectedImageUrl.set(null);
      return;
    }
    const cachedImage: CachedImage | undefined = this.images.get(imageUrl);
    if (cachedImage?.loaded) {
      this.selectedImageUrl.set(imageUrl);
    } else if (!cachedImage) {
      const image: HTMLImageElement = new Image();
      const entry: CachedImage = {image, loaded: false};
      this.images.set(imageUrl, entry);
      image.onload = (): void => {
        entry.loaded = true;
        if (this.requestedImageUrl === imageUrl) {
          this.selectedImageUrl.set(imageUrl);
        }
      };
      image.onerror = (): void => {
        console.warn(Util.logPrefix('Webapp', 'VehiclePreview') + 'Could not load a rotation image; keeping the static preview.');
        this.rotationFailed.set(true);
        this.selectedImageUrl.set(null);
        this.endInteraction();
        this.clearImages();
      };
      image.src = imageUrl;
    }
  }

  private clearImages(): void {
    this.requestedImageUrl = null;
    this.images.forEach(({image}: CachedImage): void => {
      image.onload = null;
      image.onerror = null;
    });
    this.images.clear();
  }
}
